import React from 'react';
import { Shield, Mail, Phone, MapPin, MessageSquare } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import Seo from './Seo';

const BUSINESS = 'Bubbles & Bark LLC';
const ADDRESS = '12188 Lake Underhill Rd, Orlando, FL 32828';
const EMAIL = 'info@bubblesbark.com';
const PHONE = '(407) 868-8608';
const PHONE_TEL = 'tel:+14078688608';
const EFFECTIVE = 'September 9, 2026';

function Section({ n, title, children }) {
  return (
    <section>
      <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 shrink-0">
          <span className="text-blue-600 font-bold text-sm">{n}</span>
        </div>
        {title}
      </h2>
      <div className="space-y-4 text-gray-700">{children}</div>
    </section>
  );
}

const Bullets = ({ items }) => (
  <ul className="list-disc pl-6 space-y-2">
    {items.map((t, i) => <li key={i}>{t}</li>)}
  </ul>
);

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-cyan-50">
      <Seo title="Privacy Policy" path="/privacy-policy" description="How Bubbles & Bark collects, uses and protects your information." />
      <Header showContactInfo={true} showLoginButton={true} />

      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <Shield className="w-8 h-8 text-blue-600" />
            <h1 className="text-4xl font-bold text-gray-800">Privacy Policy</h1>
          </div>
          <p className="text-lg text-gray-600">{BUSINESS}</p>
          <p className="text-sm text-gray-500">{ADDRESS}</p>
          <p className="text-sm text-gray-500 mt-2">Effective Date: {EFFECTIVE}</p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-8 space-y-8">
          <p className="text-gray-700">
            {BUSINESS} (&ldquo;Bubbles &amp; Bark,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) respects your
            privacy. This Privacy Policy explains how we collect, use, disclose, and protect information about you when you
            visit our website, use our services, or communicate with us, including via text message (SMS).
          </p>

          <Section n="1" title="Information We Collect">
            <p>We may collect the following types of information:</p>
            <Bullets items={[
              <><strong>Contact information:</strong> name, phone number, email address, mailing address</>,
              <><strong>Pet information:</strong> pet name, breed, age, grooming/service history, medical or behavioral notes relevant to grooming</>,
              <><strong>Appointment and transaction information:</strong> services booked, purchase history, payment details (processed securely by our payment processor)</>,
              <><strong>Communications:</strong> messages you send us via phone, email, text, or our website contact forms</>,
              <><strong>Website usage data:</strong> IP address, browser type, pages visited, collected via cookies or similar technologies</>,
            ]} />
          </Section>

          <Section n="2" title="How We Use Your Information">
            <p>We use the information we collect to:</p>
            <Bullets items={[
              'Schedule and provide grooming, bakery, and related services',
              'Process payments and manage your account',
              'Respond to inquiries and provide customer support',
              'Send appointment confirmations, reminders, and service updates',
              'Send promotional offers and marketing communications, where you have opted in',
              'Improve our website, services, and customer experience',
              'Comply with legal obligations',
            ]} />
          </Section>

          <Section n="3" title="Text Messaging (SMS) Program">
            <p>
              If you opt in to receive text messages from Bubbles &amp; Bark (for appointment reminders, promotions, or other
              updates):
            </p>
            <Bullets items={[
              <><strong>Consent:</strong> By providing your mobile number and opting in, you consent to receive recurring automated or manual text messages from Bubbles &amp; Bark related to appointments, services, and promotions. Consent to receive texts is not a condition of purchasing any goods or services.</>,
              <><strong>No sharing of mobile opt-in data:</strong> Mobile opt-in data and mobile phone numbers collected for the purposes of text messaging will not be shared with, sold, or disclosed to any third parties or affiliates for their own marketing purposes, under any circumstances. This information is used solely by Bubbles &amp; Bark to send you the text messages you have opted in to receive.</>,
              <><strong>Message frequency and fees:</strong> Message frequency may vary. Message and data rates may apply.</>,
              <><strong>Opt-out:</strong> You may opt out at any time by replying STOP to any text message. You will receive a one-time confirmation message. For help, reply HELP or contact us at the phone number or email listed below.</>,
              <><strong>Carrier disclaimer:</strong> Carriers are not liable for delayed or undelivered messages.</>,
            ]} />
          </Section>

          <Section n="4" title="How We Share Information">
            <p>Other than as described in Section 3 above regarding SMS opt-in data, we may share information with:</p>
            <Bullets items={[
              'Service providers who help us operate our business (e.g., payment processors, scheduling software, marketing platforms), bound by confidentiality obligations',
              'Legal authorities, if required to comply with a law, regulation, or legal process',
              'Business transfer recipients, in the event of a merger, acquisition, or sale of assets',
            ]} />
            <p className="font-semibold">We do not sell your personal information.</p>
          </Section>

          <Section n="5" title="Cookies and Tracking Technologies">
            <p>
              Our website may use cookies and similar technologies to improve functionality and analyze site traffic. You can
              control cookie preferences through your browser settings.
            </p>
          </Section>

          <Section n="6" title="Data Security">
            <p>
              We use reasonable administrative, technical, and physical safeguards to protect your information. However, no
              method of transmission or storage is completely secure, and we cannot guarantee absolute security.
            </p>
          </Section>

          <Section n="7" title="Data Retention">
            <p>
              We retain personal information for as long as necessary to provide services, comply with legal obligations,
              resolve disputes, and enforce agreements.
            </p>
          </Section>

          <Section n="8" title="Your Choices and Rights">
            <p>You may:</p>
            <Bullets items={[
              'Request access to, correction of, or deletion of your personal information',
              'Opt out of marketing communications (email or SMS) at any time',
              'Decline cookies through your browser settings',
            ]} />
            <p>To exercise these rights, contact us using the information below.</p>
          </Section>

          <Section n="9" title="Children's Privacy">
            <p>
              Our services are not directed to children under 13, and we do not knowingly collect personal information from
              children under 13.
            </p>
          </Section>

          <Section n="10" title="Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated
              effective date.
            </p>
          </Section>

          <section className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-2xl p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <MessageSquare className="w-6 h-6 text-blue-600 mr-3" />
              11. Contact Us
            </h2>
            <div className="space-y-4 text-gray-700">
              <p className="font-semibold">{BUSINESS}</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <MapPin className="w-5 h-5 text-red-600 shrink-0" />
                    <span>12188 Lake Underhill Rd<br />Orlando, FL 32828</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-blue-600 shrink-0" />
                    <a className="hover:underline" href={`mailto:${EMAIL}`}>{EMAIL}</a>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-green-600 shrink-0" />
                    <a className="hover:underline" href={PHONE_TEL}>{PHONE}</a>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
}
